import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BellOff, BellRing } from 'lucide-react';
import {
  type NotificationType,
  type NotificationPreferences,
  ALL_NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_LABELS,
} from '@iomi/shared';
import { useUserProfile } from '../context/UserProfileContext';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

export function NotificationPreferencesPage() {
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId;

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    api
      .get<{ success: boolean; data: NotificationPreferences }>(`/me/notification-preferences/${orgId}`)
      .then(res => setPrefs(res.data))
      .catch(() => setError('Failed to load preferences.'))
      .finally(() => setLoading(false));
  }, [orgId]);

  function toggle(type: NotificationType) {
    if (!prefs) return;
    setPrefs({ ...prefs, [type]: !prefs[type] });
    setDirty(true);
  }

  function selectAll() {
    if (!prefs) return;
    const updated = { ...prefs };
    for (const t of ALL_NOTIFICATION_TYPES) updated[t] = true;
    setPrefs(updated);
    setDirty(true);
  }

  function deselectAll() {
    if (!prefs) return;
    const updated = { ...prefs };
    for (const t of ALL_NOTIFICATION_TYPES) updated[t] = false;
    setPrefs(updated);
    setDirty(true);
  }

  async function handleSave() {
    if (!orgId || !prefs) return;
    setSaving(true);
    setError('');
    try {
      await api.put(`/me/notification-preferences/${orgId}`, prefs);
      setDirty(false);
    } catch {
      setError('Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  }

  const allOn = prefs ? ALL_NOTIFICATION_TYPES.every(t => prefs[t]) : false;
  const allOff = prefs ? ALL_NOTIFICATION_TYPES.every(t => !prefs[t]) : false;

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl md:mx-auto">
      {/* Header */}
      <button
        type="button"
        onClick={() => navigate('/profile')}
        className="flex items-center gap-1.5 text-[13px] text-slate font-semibold mb-4
                   hover:text-plum transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Profile
      </button>

      <h1 className="font-display text-h1 text-primary-theme mb-1">
        Email Notification Preferences
      </h1>
      <p className="text-[13px] text-slate mb-6">
        Choose which email notifications you receive{activeOrg ? ` for ${activeOrg.orgName}` : ''}.
      </p>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && !prefs && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">{error}</p>
        </div>
      )}

      {!loading && prefs && (
        <div className="flex flex-col gap-5">
          {/* Select / Deselect all */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={selectAll}
              disabled={allOn}
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-stone text-[13px]
                         font-semibold text-primary-theme
                         hover:border-plum/40 hover:text-plum active:bg-mist disabled:opacity-40
                         transition-all duration-150"
            >
              <BellRing size={14} />
              Select All
            </button>
            <button
              type="button"
              onClick={deselectAll}
              disabled={allOff}
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-stone text-[13px]
                         font-semibold text-primary-theme
                         hover:border-plum/40 hover:text-plum active:bg-mist disabled:opacity-40
                         transition-all duration-150"
            >
              <BellOff size={14} />
              Deselect All
            </button>
          </div>

          {/* Toggles */}
          <div className="bg-surface border border-stone rounded-xl shadow-brand-sm divide-y divide-stone">
            {ALL_NOTIFICATION_TYPES.map(type => (
              <label
                key={type}
                className="flex items-center justify-between px-4 py-3.5 cursor-pointer
                           hover:bg-mist/50 transition-colors first:rounded-t-xl last:rounded-b-xl"
              >
                <span className="text-[14px] text-primary-theme select-none">
                  {NOTIFICATION_TYPE_LABELS[type]}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={prefs[type]}
                  onClick={() => toggle(type)}
                  className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent
                             transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2
                             focus:ring-plum/20 focus:ring-offset-2
                             ${prefs[type] ? 'bg-plum' : 'bg-stone'}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow
                               transform transition-transform duration-200 ease-in-out
                               ${prefs[type] ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </label>
            ))}
          </div>

          {/* Error */}
          {error && prefs && (
            <p className="text-[13px] text-rose">{error}</p>
          )}

          {/* Save */}
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="h-11 rounded-lg bg-plum text-white text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 flex items-center justify-center gap-2"
          >
            {saving ? <LoadingSpinner size="sm" /> : 'Save Preferences'}
          </button>
        </div>
      )}
    </div>
  );
}
